/* assets/src/viewer/models/controller.js
 * PergolaController: instrada il cambio config al costo minimo (diff-based).
 *  - topological -> rebuild del solo gruppo modello (raro)
 *  - dimensional -> model.update() IN-PLACE (nessun dispose, nessun reframe camera)
 *  - aspect      -> cross-fade colore in-place
 *  - effects     -> hook OptionalEffects (task 9)
 * Strumentato con Stats per l'acceptance (fullRebuilds non cresce sul dimensionale).
 */
import * as THREE from 'three';
import { normalizeConfig, classify } from '../config/diff.js';
import { computeAdaptiveFov } from '../core/camera.js';
import { disposeGroup } from '../perf/disposal.js';
import { Stats } from '../perf/stats.js';
import { LouversModel } from './louvers.js';
import { GenericPergolaModel } from './generic.js';
import { AwningArmModel, Awning8000EvoModel, AwningCappottinaModel, AwningMollaModel } from './awnings.js';

function makeModel(type, materials, colors) {
  if (type === 'louvers') return new LouversModel(materials, colors);
  if (type === 'glass') return new GenericPergolaModel(materials, colors, { type: 'glass' });
  if (type === 'awning_arm') return new AwningArmModel(materials, colors);
  if (type === 'awning_arm_8000evo') return new Awning8000EvoModel(materials, colors);
  if (type === 'awning_cappottina') return new AwningCappottinaModel(materials, colors);
  if (type === 'awning_molla' || type === 'awning_fall') return new AwningMollaModel(materials, colors);
  // fabric + fallback emergency -> pergola generica con telo
  return new GenericPergolaModel(materials, colors, { type: 'fabric' });
}

export class PergolaController {
  constructor(viewer, materials) {
    this.viewer = viewer;
    this.materials = materials;
    this.stats = new Stats();
    this.model = null;
    this.prevNorm = null;
    this.onEffects = null; // impostato dal modulo OptionalEffects
  }

  _dims(norm) {
    // Port visibility scale (:1708-1710): ingrandisce i modelli piccoli per leggibilità.
    const baseW = norm.larghezza / 100;
    const baseD = norm.sporgenza / 100;
    const visibilityScale = Math.max(1, 2 / Math.max(baseW, baseD, 0.1));
    return { w: baseW * visibilityScale, d: baseD * visibilityScale };
  }

  applyConfig(config = {}) {
    const norm = normalizeConfig(config);
    const diff = classify(this.prevNorm, norm);
    const dims = this._dims(norm);
    const colors = { structColor: norm.structColor, fabricColor: norm.fabricColor };

    try {
      if (diff.topological || !this.model) {
        this._rebuild(norm, dims, colors);
        this.stats.inc('topological');
      } else {
        if (diff.dimensional) { this.model.update(dims, norm.raw); this.stats.inc('dimensional'); }
        if (diff.aspect) {
          if (this.model.setStructureColor) this.model.setStructureColor(colors.structColor);
          if (this.model.setFabricColor) this.model.setFabricColor(colors.fabricColor);
          this.stats.inc('aspect');
        }
        if (diff.effects) this.stats.inc('effects');
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ASG Viewer F6] applyConfig: rebuild di emergenza', e);
      this._emergency(dims, colors);
    }

    // Optional -> 3D: applica/aggiorna gli effetti quando cambiano o si (ri)costruisce.
    if (this.onEffects && (diff.effects || diff.topological || diff.first)) {
      try { this.onEffects(norm.raw, this.model, dims); } catch (e) { /* effetti non bloccanti */ }
    }

    // Arredi: sincronizza visibilità su rebuild (alcuni modelli, es. cappottina, li disabilitano)
    // e riposiziona sul ridimensionamento.
    if (this.viewer.furniture) {
      if (diff.topological || diff.first) this.viewer.furniture.setVisible(window.ASG_FurnitureActive !== false);
      else if (diff.dimensional) this.viewer.furniture.onSceneResized();
    }

    // FOV adattivo
    const fov = computeAdaptiveFov(norm.raw, dims.w, dims.d, norm.modelType);
    if (this.viewer.camera && Math.abs(this.viewer.camera.fov - fov) > 0.1) {
      this.viewer.camera.fov = fov;
      this.viewer.camera.updateProjectionMatrix();
    }

    // Framing: riposiziona la camera SOLO su rebuild/primo; sul dimensionale aggiorna
    // solo bounds + contact shadow (camera ferma => fluido).
    if (diff.topological || diff.first) {
      this.viewer.currentModelType = norm.modelType;
      const box = new THREE.Box3().setFromObject(this.model.group);
      this.viewer.fit(box, { preserveView: false });
    } else if (diff.dimensional) {
      // Dimensionale: aggiorna bounds + contact shadow; camera ferma => resize fluido in-place (niente morph).
      const box = new THREE.Box3().setFromObject(this.model.group);
      if (!box.isEmpty()) {
        this.viewer.currentSceneBounds = box;
        this.viewer.sceneManager.fitContactShadow(box);
      }
    }

    this.viewer.hasActiveModel = true;
    this.prevNorm = norm;
    this.viewer.requestRender();
    return this.stats.snapshot();
  }

  _rebuild(norm, dims, colors) {
    this._destroyModel();
    this.model = makeModel(norm.modelType, this.materials, colors);
    this.viewer.currentModelType = norm.modelType;
    this.model.build(dims, norm.raw);
    this.viewer.scene.add(this.model.group);
    this._wireFabric();
  }

  _emergency(dims, colors) {
    this._destroyModel();
    this.model = new GenericPergolaModel(this.materials, colors, { type: 'fabric' });
    this.model.build(dims, {});
    this.viewer.scene.add(this.model.group);
    this._wireFabric();
  }

  _wireFabric() {
    window.activeFabricGroup = this.model.fabricGroup || null;
    if (typeof window.ASG_TeloProgress === 'undefined') {
      window.ASG_TeloProgress = this.model.type === 'louvers' ? 0 : 1;
    }
    const fg = window.activeFabricGroup;
    if (fg && fg.userData.updateLayout) fg.userData.updateLayout(window.ASG_TeloProgress);
  }

  _destroyModel() {
    if (this.model) {
      this.viewer.scene.remove(this.model.group);
      disposeGroup(this.model.group);
      if (this.model.dispose) this.model.dispose();
      this.model = null;
    }
  }

  clear() {
    this._destroyModel();
    window.activeFabricGroup = null;
    this.prevNorm = null;
    this.viewer.hasActiveModel = false;
    if (this.viewer.sceneManager.contactShadow) this.viewer.sceneManager.contactShadow.visible = false;
    this.viewer.requestRender();
  }

  getStats() { return this.stats.snapshot(); }
}
